import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  blacklistError,
  BrokerCheck,
  BrokerBlacklistEntry,
  BrokerMatch,
  brokerBlacklistService,
} from "../../../api/brokerBlacklistService";
import useAuth from "../../../hooks/useAuth";
import BrokerBlacklistDetails from "./BrokerBlacklistDetails";

export default function BrokerBlacklistWarning({
  input,
  onCheckingChange,
}: {
  input: BrokerCheck;
  onCheckingChange?: (checking: boolean) => void;
}) {
  const { token } = useAuth();
  const [result, setResult] = useState<{
    key: string;
    matches?: BrokerMatch[];
    error?: string;
  }>();
  const [details, setDetails] = useState<BrokerBlacklistEntry | null>(null);
  const key = JSON.stringify(input);
  const hasInput = Object.entries(input).some(
    ([field, value]) => field !== "list" && value?.trim(),
  );
  const current = result?.key === key ? result : undefined;
  const checking = hasInput && !current;
  useEffect(() => {
    onCheckingChange?.(checking);
  }, [checking, onCheckingChange]);
  useEffect(() => {
    if (!token || !hasInput) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      brokerBlacklistService
        .check(token, JSON.parse(key), controller.signal)
        .then((matches) => {
          if (!controller.signal.aborted) setResult({ key, matches });
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setResult({ key, error: blacklistError(error) });
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [key, hasInput, token]);
  if (!hasInput) return null;
  return (
    <Stack spacing={1} aria-live="polite">
      {!current && (
        <Typography variant="body2">
          <CircularProgress size={14} /> Checking broker blacklists…
        </Typography>
      )}
      {current?.error && (
        <Alert severity="error">
          Blacklist check unavailable: {current.error}
        </Alert>
      )}
      {current?.matches?.length === 0 && (
        <Alert severity="info">
          No matching broker found in{" "}
          {input.list ? `${input.list} blacklist` : "C1 or Fox blacklists"}.
        </Alert>
      )}
      {current?.matches?.map((match) => (
        <Alert
          severity="warning"
          key={match.entry.id}
          action={
            <Button color="inherit" onClick={() => setDetails(match.entry)}>
              Reason & source email
            </Button>
          }
        >
          <strong>
            Blacklisted broker — {match.entry.list}: {match.entry.name}
          </strong>
          <Typography variant="body2">
            Matched on {match.matchedOn.join(", ")}.{" "}
            {match.entry.reason || "No reason provided."}
          </Typography>
        </Alert>
      ))}
      <BrokerBlacklistDetails
        entry={details}
        onClose={() => setDetails(null)}
      />
    </Stack>
  );
}
